import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PayrollEvidence, PayrollHistoryRow, PayrollHistoryStatus, StatementRef } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { Chip, Dialog, ErrorBox, Field, FileDrop, Kpi, Notice, Page, Spinner } from "../../expenses/components/ui";
import StatementViewer from "../../expenses/components/StatementViewer";
import { currentMonth, money, monthLabel, shiftMonth } from "../../expenses/lib/format";
import { apiErrorMessage } from "../../expenses/services/expensesService";
import { fetchPayrollHistory, uploadStatement, type PayrollHistoryResponse } from "../../expenses/services/statementVaultService";
import { ui } from "../../expenses/theme";

// ---------------------------------------------------------------------------
// Historial de nómina pagada (#1030 / épica #1027) — SOLO DUEÑOS.
//
// Cada mes y cada grupo de pago (dispersión NVIO o pago directo a cada
// colaborador) contra el movimiento real del ESTADO DE CUENTA donde salió el
// dinero. Si no aparece en ningún estado de cuenta cargado: «Sin comprobante»,
// en rojo. Un gasto capturado a mano no cuenta como comprobante.
//
// El estado de cuenta se abre cifrado: el servidor pide un código que llega al
// correo del dueño (StatementViewer).
// ---------------------------------------------------------------------------

const RANGES = [6, 12, 24];

export const STATUS_TONES: Record<PayrollHistoryStatus, string> = {
  conciliada: "bg-emerald-500/15 text-emerald-600",
  diferencia: "bg-amber-500/15 text-amber-600",
  por_revisar: "bg-sky-500/15 text-sky-600",
  sin_comprobante: "bg-red-600 text-white",
  en_curso: "bg-gray-400/15 text-gray-500",
};

/** Clave de un estado de cuenta: su documento, si no su importación, si no su etiqueta. */
const statementKey = (s: StatementRef) => s.id || (s.importId ? `import:${s.importId}` : `label:${s.label.toLowerCase()}`);

/** Estados de cuenta distintos de los pagos de una fila. */
function statementsOf(evidence: PayrollEvidence[]): StatementRef[] {
  const seen = new Map<string, StatementRef>();
  for (const e of evidence) if (e.statement && !seen.has(statementKey(e.statement))) seen.set(statementKey(e.statement), e.statement);
  return [...seen.values()];
}

export default function PaidHistorySection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const lang = i18n.language;
  const [months, setMonths] = useState(12);
  const [data, setData] = useState<PayrollHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [viewing, setViewing] = useState<{ id: string; label: string } | null>(null);
  const [upload, setUpload] = useState<{ label: string; file: File | null } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const to = currentMonth();
    try {
      setData(await fetchPayrollHistory({ from: shiftMonth(to, -(months - 1)), to }));
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("payroll.paidHistory.errors.load"));
    }
  }, [months, t]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  const saveUpload = async () => {
    if (!upload?.file || !upload.label.trim()) return;
    setUploading(true);
    setUploadError(null);
    try {
      await uploadStatement(upload.file, upload.label.trim());
      setUpload(null);
      await load();
    } catch (err) {
      setUploadError(apiErrorMessage(err) || t("payroll.paidHistory.errors.upload"));
    } finally {
      setUploading(false);
    }
  };

  const expectedText = (r: PayrollHistoryRow) =>
    Object.entries(r.expected)
      .map(([cur, v]) => money(v, cur, lang))
      .join(" + ") || "—";

  const StatementLinks = ({ row }: { row: PayrollHistoryRow }) => {
    const list = statementsOf(row.evidence);
    if (!list.length) return <span className={`text-xs ${c.muted}`}>—</span>;
    return (
      <div className="flex flex-col items-start gap-1">
        {list.map((s) =>
          s.id && s.hasFile ? (
            <button key={statementKey(s)} type="button" className="flex items-center gap-1 text-xs text-teal-600 hover:underline" onClick={() => setViewing({ id: s.id!, label: s.label })}>
              <i className="ri-lock-2-line" />
              {s.label}
            </button>
          ) : s.importId ? (
            // Importación con IA anterior al cifrado: se cifra con el script, no se vuelve a subir.
            <span key={statementKey(s)} className={`text-xs ${c.muted}`} title={t("payroll.paidHistory.pendingEncryptionHint")}>
              {s.label} · {t("payroll.paidHistory.pendingEncryption")}
            </span>
          ) : (
            <span key={statementKey(s)} className={`flex items-center gap-1 text-xs ${c.muted}`}>
              {s.label}
              <button type="button" className="text-teal-600 hover:underline" onClick={() => setUpload({ label: s.label, file: null })}>
                {t("payroll.paidHistory.uploadFile")}
              </button>
            </span>
          ),
        )}
      </div>
    );
  };

  const totals = data?.totals;
  return (
    <Page
      title={t("payroll.paidHistory.title")}
      subtitle={t("payroll.paidHistory.subtitle")}
      actions={
        <>
          <select className={`${c.inputBase} w-auto`} value={months} onChange={(e) => setMonths(Number(e.target.value))} aria-label={t("payroll.paidHistory.range")}>
            {RANGES.map((n) => (
              <option key={n} value={n}>
                {t("payroll.paidHistory.lastMonths", { count: n })}
              </option>
            ))}
          </select>
          <button type="button" className={c.secondary} onClick={() => setUpload({ label: "", file: null })}>
            <i className="ri-upload-2-line mr-1" />
            {t("payroll.paidHistory.uploadStatement")}
          </button>
        </>
      }
    >
      <ErrorBox message={error} />
      {!data && !error && <Spinner label={t("payroll.paidHistory.loading")} />}
      {data && totals && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={t("payroll.paidHistory.status.conciliada")} value={String(totals.conciliada)} tone="good" />
            <Kpi label={t("payroll.paidHistory.status.diferencia")} value={String(totals.diferencia)} />
            <Kpi label={t("payroll.paidHistory.status.por_revisar")} value={String(totals.por_revisar)} />
            <Kpi label={t("payroll.paidHistory.status.sin_comprobante")} value={String(totals.sin_comprobante)} tone={totals.sin_comprobante ? "bad" : undefined} />
          </div>
          <Notice>{t("payroll.paidHistory.howItWorks", { pct: Math.round(data.tolerance * 100) })}</Notice>

          <div className={`${c.card} overflow-x-auto p-0`}>
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className={`border-b text-left text-xs ${c.border} ${c.muted}`}>
                  <th className="px-3 py-2">{t("payroll.paidHistory.cols.month")}</th>
                  <th className="px-3 py-2">{t("payroll.paidHistory.cols.payment")}</th>
                  <th className="px-3 py-2 text-right">{t("payroll.paidHistory.cols.expected")}</th>
                  <th className="px-3 py-2 text-right">{t("payroll.paidHistory.cols.paid")}</th>
                  <th className="px-3 py-2">{t("payroll.paidHistory.cols.status")}</th>
                  <th className="px-3 py-2">{t("payroll.paidHistory.cols.statement")}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr className={`border-b ${c.border} ${c.rowHover} ${r.status === "sin_comprobante" ? "bg-red-500/5" : ""}`}>
                      <td className={`whitespace-nowrap px-3 py-2 ${c.text}`}>{monthLabel(r.period, lang)}</td>
                      <td className="px-3 py-2">
                        <button type="button" className={`text-left ${c.text}`} onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))} aria-expanded={!!open[r.id]}>
                          <i className={`${open[r.id] ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} mr-1`} />
                          {r.group === "dispersion" ? `${r.label} · ${t("payroll.paidHistory.dispersion")}` : r.label}
                        </button>
                        {r.group === "dispersion" && <p className={`text-xs ${c.muted}`}>{r.members.map((m) => m.name).join(", ")}</p>}
                      </td>
                      <td className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${c.text}`}>
                        {expectedText(r)}
                        <p className={`text-[11px] ${c.muted}`}>{t(`payroll.paidHistory.source.${r.expectedSource}`)}</p>
                      </td>
                      <td className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${c.text}`}>
                        {r.evidence.length ? money(r.paidBase, "MXN", lang) : "—"}
                        {r.diffPct != null && (
                          <p className={`text-[11px] ${Math.abs(r.diffPct) > data.tolerance ? "text-amber-600" : c.muted}`}>
                            {r.diffPct > 0 ? "+" : ""}
                            {(r.diffPct * 100).toFixed(1)} %
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Chip className={STATUS_TONES[r.status]}>{t(`payroll.paidHistory.status.${r.status}`)}</Chip>
                      </td>
                      <td className="px-3 py-2">
                        <StatementLinks row={r} />
                      </td>
                    </tr>
                    {open[r.id] && (
                      <tr className={`border-b ${c.border} ${c.surface}`}>
                        <td colSpan={6} className="px-6 py-2">
                          {r.evidence.length ? (
                            <ul className="space-y-1 text-xs">
                              {r.evidence.map((e) => (
                                <li key={e.expenseId} className={`flex flex-wrap gap-x-3 ${c.text}`}>
                                  <span className="tabular-nums">{e.date}</span>
                                  <span className="tabular-nums">{money(e.amount, e.currency, lang)}</span>
                                  <span className={c.muted}>{e.description}</span>
                                  {e.statement && <span className={c.muted}>· {e.statement.label}</span>}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-red-500">{t("payroll.paidHistory.noEvidence")}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className={`px-3 py-6 text-center text-sm ${c.muted}`}>
                      {t("payroll.paidHistory.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data.unassigned.length > 0 && (
            <div className={c.card}>
              <h3 className={`mb-1 text-sm font-semibold ${c.text}`}>{t("payroll.paidHistory.unassigned")}</h3>
              <p className={`mb-2 text-xs ${c.muted}`}>{t("payroll.paidHistory.unassignedHint")}</p>
              <ul className="space-y-1 text-xs">
                {data.unassigned.map((e) => (
                  <li key={e.expenseId} className={`flex flex-wrap gap-x-3 ${c.text}`}>
                    <span className="tabular-nums">{e.date}</span>
                    <span className="tabular-nums">{money(e.amount, e.currency, lang)}</span>
                    <span className={c.muted}>{e.description}</span>
                    {e.statement && <span className={c.muted}>· {e.statement.label}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <StatementViewer statementId={viewing?.id || null} label={viewing?.label} onClose={() => setViewing(null)} />

      <Dialog
        open={!!upload}
        title={t("payroll.paidHistory.uploadStatement")}
        onClose={() => setUpload(null)}
        footer={
          <>
            {uploading && <Spinner />}
            <button type="button" className={c.secondary} onClick={() => setUpload(null)}>
              {t("payroll.paidHistory.cancel")}
            </button>
            <button type="button" className={c.primary} disabled={uploading || !upload?.file || !upload?.label.trim()} onClick={saveUpload}>
              {t("payroll.paidHistory.saveEncrypted")}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <ErrorBox message={uploadError} />
          <Field label={t("payroll.paidHistory.statementLabel")}>
            <input className={c.input} value={upload?.label || ""} onChange={(e) => setUpload((u) => (u ? { ...u, label: e.target.value } : u))} placeholder="Cuenta Nu febrero 2026" />
          </Field>
          <FileDrop
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onFiles={(files) => setUpload((u) => (u ? { ...u, file: files[0] || null } : u))}
            label={upload?.file ? upload.file.name : t("payroll.paidHistory.dropFile")}
            hint={t("payroll.paidHistory.encryptedHint")}
          />
        </div>
      </Dialog>
    </Page>
  );
}
